/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Button, Icon, useToast } from 'sarsaparilla';

import * as actions from '../../actions/tripPreferences';
import TripPreferencesForm from '../../components/tripPreferences/TripPreferencesForm';
import { useAppDispatch } from '../../redux/redux-helpers';
import { ResponsiveDialog } from '../ResponsiveDialog';

import styles from './SearchFiltersDialog.module.scss';

interface EditTripPreferencesDialogProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    checked: string[];
    onChange: (checked: string[]) => void;
    preferences: string[];
    /**
     * Called with the new preferences once they're saved to the account and,
     * if trip preferences are applied, to the search
     */
    onSaved: (preferences: string[]) => void;
}

/**
 * Sets up or edits the user's trip preferences in a dialog nested in the
 * filters dialog. Saves the same way as the desktop trip preferences modal: if
 * the applied search uses trip preferences, the new ones replace the old ones
 * there too and the search re-runs, since closing the filters dialog without
 * showing results would otherwise leave the old ones applied.
 */
export function EditTripPreferencesDialog({
    isOpen,
    onOpenChange,
    checked,
    onChange,
    preferences,
    onSaved,
}: EditTripPreferencesDialogProps) {
    const showToast = useToast();
    const [loading, setLoading] = React.useState<boolean>(false);
    const dispatch = useAppDispatch();

    const closeDialog = () => {
        onOpenChange(false);
    };

    const updateTripPreferences = async (): Promise<void> => {
        setLoading(true);
        try {
            await dispatch(actions.saveTripPreferences(checked));
            onSaved(checked);
            setLoading(false);
            closeDialog();
            showToast('Success! Your trip preferences have been updated!', {
                type: 'success',
            });
        } catch {
            showToast(
                'There was a problem updating your trip preferences, please try again.',
                {
                    type: 'error',
                }
            );
            setLoading(false);
            closeDialog();
        }
    };

    return (
        <ResponsiveDialog.Root open={isOpen} onOpenChange={onOpenChange}>
            <ResponsiveDialog.Portal className={styles.Portal}>
                <ResponsiveDialog.Viewport className={styles.Viewport}>
                    <ResponsiveDialog.Popup className={styles.Popup}>
                        <ResponsiveDialog.Handle className={styles.Handle} />
                        <ResponsiveDialog.Content className={styles.Content}>
                            <div className={styles.Header}>
                                <ResponsiveDialog.Title className={styles.Title}>
                                    Edit Trip Preferences
                                </ResponsiveDialog.Title>
                                <ResponsiveDialog.Close
                                    aria-label="Close Trip Preferences"
                                    className={styles.Close}
                                >
                                    <Icon iconName="close" />
                                </ResponsiveDialog.Close>
                            </div>

                            <div className={styles.Body}>
                                <div className={styles.BodyViewport}>
                                    <p className="pb-2">
                                        Set your preferences below to keep your searches
                                        focused on campsites of interest to you. We will
                                        also use your preferences to improve the
                                        recommendations we provide.
                                    </p>

                                    <TripPreferencesForm
                                        onChange={onChange}
                                        checked={checked}
                                    />
                                </div>
                            </div>

                            <div className={styles.Actions}>
                                <Button
                                    appearance="subtle"
                                    onClick={closeDialog}
                                    isDisabled={loading}
                                    clickTagCategory="Search"
                                    clickTagAction={
                                        preferences?.length > 0
                                            ? `Cancel / Active Filters - ${preferences}`
                                            : `No Preferences Selected`
                                    }
                                    clickTagLabel="Trip Preferences Modal"
                                >
                                    Cancel
                                </Button>

                                <Button
                                    appearance="primary"
                                    onClick={updateTripPreferences}
                                    isLoading={loading}
                                    clickTagCategory="Search"
                                    clickTagAction={
                                        checked?.length > 0
                                            ? `Selected / Active Filters - ${checked}`
                                            : `No Preferences Selected`
                                    }
                                    clickTagLabel="Trip Preferences Modal"
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </ResponsiveDialog.Content>
                    </ResponsiveDialog.Popup>
                </ResponsiveDialog.Viewport>
            </ResponsiveDialog.Portal>
        </ResponsiveDialog.Root>
    );
}
